import React, { Fragment, useEffect, useState } from "react";
import { Box, Typography, Paper, Grid, Divider } from "@mui/material";
import { useAxios, useComponent } from "../../hooks";
import { useLocation, useNavigate } from "react-router-dom";
import { Loader, LoadingButton } from "../../components";
import { getVisitorFaceData } from "../../utils/getVisitorFaceData";

const AppointmentPage = () => {
  const axios = useAxios();
  const navigate = useNavigate();
  const { state, search } = useLocation();
  const { loader } = useComponent();
  const queryParams = new URLSearchParams(search);

  const entityID = queryParams.get("entity_id");
  const visitorID = queryParams.get("visitor_id");
  const bookingID = queryParams.get("booking_id");
  const userTypeId = queryParams.get("user_type_id");

  const [bookingStatus, setBookingStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userData, setUserData] = useState(state);
  const [entityDetails, setEntityDetails] = useState([]);
  const [premises, setPremisesData] = useState([]);

  // ⏳ Timer for redirect
  const [timer, setTimer] = useState(5);

  useEffect(() => {
    axios({
      url: `/admin/profile/get-premises-banner?premises_id=` + entityID,
      disableRedirect: true,
      disableError: true,
    }).then((response) => {
      if (response.status) {
        setPremisesData(response.data);
      }
    });
  }, []);

  // Fetch booking status
  useEffect(() => {
    loader.start();
    axios({
      url: "admin/profile/get-visitor-booking-status",
      method: "POST",
      disableRedirect: true,
      disableError: true,
      data: {
        visitor_id: visitorID,
        entity_id: entityID,
        user_type_id: userTypeId,
        booking_id: bookingID,
        face_data: getVisitorFaceData(visitorID),
      },
    })
      .then((response) => {
        if (response.status) {
          setUserData({ userData: response.data });
          setBookingStatus(response.data[0].appointment_status);
        } else {
          setError("Failed to fetch booking status.");
        }
      })
      .catch(() => setError("Something went wrong!"))
      .finally(() => {
        loader.apiComplete();
        setLoading(false);
      });
  }, []);

  // Fetch event details
  useEffect(() => {
    axios({
      url: `admin/events/event-details?event_id=${entityID}`,
      method: "GET",
      customAuthToken: "9beaa95b-c59f-4ec9-bb60-5f4ee1986311",
    }).then((response) => {
      if (response.status) setEntityDetails(response.data);
    });
  }, [entityID]);

  // ⏲ Countdown + Redirect
  useEffect(() => {
    if (
      !loading &&
      timer > 0 &&
      userData?.userData?.[0]?.booking_redirect_url
    ) {
      const countdown = setTimeout(() => setTimer((prev) => prev - 1), 1000);
      return () => clearTimeout(countdown);
    } else if (timer === 0 && userData?.userData?.[0]?.booking_redirect_url) {
      window.location.href = userData.userData[0].booking_redirect_url;
    }
  }, [loading, timer, userData]);

  return (
    <Loader>
      <Box
        display="flex"
        justifyContent="center"
        alignItems="flex-start"
        sx={{
          background: "#ffffffff",
          fontFamily: "Fira Sans, Montserrat, sans-serif",
          py: { xs: 2, sm: 4 },
          overflowY: "auto",
        }}
      >
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          textAlign="center"
          sx={{
            width: { xs: "92%", sm: "420px" },
            maxWidth: "420px",
            mx: "auto",
          }}
        >
          {/* Event Banner */}
          <Box
            component="img"
            src={
              premises.premises_banner ||
              entityDetails.banner_image ||
              "data:image/png;base64,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"
            }
            alt="Event Banner"
            sx={{
              mt: 2,
              mb: 3,
              width: "100%",
              maxWidth: "100%",
              borderRadius: 3,
              boxShadow: 2,
              height: "25vh",
              objectFit: "contain",
              marginTop: 4,
            }}
          />

          {/* Title */}
          <Typography
            mt={4}
            variant="h4"
            fontWeight="bold"
            gutterBottom
            sx={{
              fontSize: { xs: "1.6rem", sm: "1.8rem" },
              textAlign: "center",
            }}
          >
            {bookingStatus === "pending"
              ? "⚠️ Pending Approval"
              : "🎉 All done!"}
          </Typography>

          <Typography
            variant="body1"
            fontWeight={500}
            sx={{
              fontSize: { xs: 14, sm: 16 },
              textAlign: "center",
              maxWidth: 450,
            }}
            mb={4}
          >
            {bookingStatus === "pending" ? (
              <Fragment>
                Your consent has been saved successfully.
                <br />
                We've notified the host of your visit. You'll receive a final
                pass soon!
              </Fragment>
            ) : (
              <Fragment>
                You can now check in instantly with a <b>face scan</b> at the
                gate.
              </Fragment>
            )}
          </Typography>

          {/* Show booking info */}
          {userData?.userData?.[0] && (
            <Paper
              elevation={0}
              sx={{
                border: "1px solid #E0E0E0",
                borderRadius: "12px",
                p: 2,
                mb: 3,
                backgroundColor: "#FAFAFA",
                width: "100%",
              }}
            >
              <Typography
                variant="subtitle2"
                color="text.secondary"
                mb={1}
                textAlign="left"
              >
                #1 Booking Details
              </Typography>
              <Divider />
              <Grid container alignItems="center" mt={2}>
                <Grid item xs={9} textAlign="left" mb={2}>
                  <Typography variant="body1" fontWeight="bold" noWrap>
                    {userData.userData[0]?.visitor_info?.first_name +
                      " " +
                      userData.userData[0]?.visitor_info?.last_name}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    Booking ID:{" "}
                    {userData.userData[0]?.appointment_id ||
                      userData.userData[0]?.booking_id}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          )}

          {/* Timer + Redirect Message */}
          {userData?.userData?.[0]?.booking_redirect_url && (
            <Typography variant="h6" color="text.secondary" mb={2}>
              Redirecting in {timer} seconds...
            </Typography>
          )}
        </Box>
      </Box>
    </Loader>
  );
};

export default AppointmentPage;
